import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create dedicated axios instance for client portal
const portalApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add portal token
portalApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('portal_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
portalApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('portal_token');
      localStorage.removeItem('portal_contact');
      window.location.href = '/espace-client/login';
    }
    return Promise.reject(error);
  }
);

const ClientPortalAuthContext = createContext(null);

export const useClientPortalAuth = () => {
  const context = useContext(ClientPortalAuthContext);
  if (!context) {
    throw new Error('useClientPortalAuth must be used within a ClientPortalAuthProvider');
  }
  return context;
};

export const ClientPortalAuthProvider = ({ children }) => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Admin impersonation mode: when an admin opens the portal via
  // "Voir le portail client", the URL contains ?impersonation=<jwt>.
  // We consume it here, store it like a normal portal_token, and flag
  // the session as read-only so the UI can show a banner + disable
  // write actions.
  const [isImpersonation, setIsImpersonation] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      // 1) Check for impersonation token in URL query param
      const params = new URLSearchParams(window.location.search);
      const impersonationToken = params.get('impersonation');
      if (impersonationToken) {
        // Clear the token from the URL so it doesn't leak on refresh /
        // bookmark sharing, then use it as the active portal session.
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        localStorage.setItem('portal_token', impersonationToken);
        localStorage.setItem('portal_impersonation', 'true');
        // Force removal of any prior contact data — the impersonation
        // token will be verified by the /auth/me call below.
        localStorage.removeItem('portal_contact');
      }

      // 2) Normal check — verify existing token
      const token = localStorage.getItem('portal_token');

      if (token) {
        try {
          const response = await portalApi.get('/client-portal/auth/me');
          const contactData = response.data;
          setContact(contactData);
          setIsAuthenticated(true);
          // Detect impersonation either from the just-consumed URL param
          // or from a previously stored flag (page reload while still in
          // impersonation session).
          if (impersonationToken || localStorage.getItem('portal_impersonation') === 'true' || contactData?.admin_impersonation) {
            setIsImpersonation(true);
          }
        } catch (error) {
          localStorage.removeItem('portal_token');
          localStorage.removeItem('portal_contact');
          localStorage.removeItem('portal_impersonation');
          setContact(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await portalApi.post('/client-portal/auth/login', { email, password });
      const { access_token, contact: contactData } = response.data;
      
      localStorage.setItem('portal_token', access_token);
      localStorage.setItem('portal_contact', JSON.stringify(contactData));
      
      setContact(contactData);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Erreur de connexion'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_contact');
    localStorage.removeItem('portal_impersonation');
    setContact(null);
    setIsAuthenticated(false);
    setIsImpersonation(false);
  };

  const value = {
    contact,
    loading,
    isAuthenticated,
    isImpersonation,
    login,
    logout,
    portalApi
  };

  return (
    <ClientPortalAuthContext.Provider value={value}>
      {children}
    </ClientPortalAuthContext.Provider>
  );
};

export { portalApi };
